import { checkEmpty, readStoredJson } from "./GenericFunctions";
import { clearApiToken } from "./ApiPaths";

const LOGGED_IN_KEY = "isLoggedIn";
const EXPIRES_KEY = "pat_sessionExpiresAt";

/** Midnight end of the patient's local calendar day. */
export function endOfLocalDayMs(from = Date.now()) {
  const d = new Date(from);
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

function hasPatientIdentity() {
  const login = readStoredJson("pat_loginDataVo");
  const patLoc = readStoredJson("pat_Sel_LocationVo");
  return !checkEmpty(login) || !checkEmpty(patLoc);
}

/**
 * Call on successful patient sign-in / registration.
 * Session lasts until end of the local day (no short idle logout).
 */
export function establishPatientSession() {
  try {
    localStorage.setItem(LOGGED_IN_KEY, "true");
    localStorage.setItem(EXPIRES_KEY, String(endOfLocalDayMs()));
  } catch (e) {
    /* ignore quota / private mode */
  }
}

/**
 * True when signed in and a hospital patient identity is available
 * (needed for book / cart / checkout — catalog browse does not need this).
 */
export function hasBookablePatientContext() {
  if (!isPatientSessionActive()) return false;
  const pat = readStoredJson("pat_Sel_LocationVo");
  if (checkEmpty(pat)) return false;
  return !!(
    pat.patientNo ||
    pat.accountNo ||
    pat.accountKey ||
    pat.accountkey
  );
}

/** True when patient is signed in and the day-session has not ended. */
export function isPatientSessionActive() {
  try {
    const flag = localStorage.getItem(LOGGED_IN_KEY);
    const expRaw = localStorage.getItem(EXPIRES_KEY);
    const exp = expRaw ? parseInt(expRaw, 10) : 0;

    if (exp && Date.now() > exp) {
      clearPatientSession({ keepLocation: true });
      return false;
    }

    if (flag === "true" && hasPatientIdentity()) {
      // Legacy sessions without expiry: grant the rest of today
      if (!exp) {
        localStorage.setItem(EXPIRES_KEY, String(endOfLocalDayMs()));
      }
      return true;
    }

    // Identity present but flag missing (older flows) — treat as logged in for today
    if (hasPatientIdentity()) {
      establishPatientSession();
      return true;
    }
  } catch (e) {
    return false;
  }
  return false;
}

/**
 * Explicit logout. Optionally keep hospital selection for public browse.
 */
export function clearPatientSession({ keepLocation = false } = {}) {
  try {
    const countryVo = localStorage.getItem("countryVo");
    const clientLocURL = localStorage.getItem("clientLocURL");
    const selectedLocationVo = keepLocation
      ? localStorage.getItem("selectedLocationVo")
      : null;

    localStorage.removeItem(LOGGED_IN_KEY);
    localStorage.removeItem(EXPIRES_KEY);
    localStorage.removeItem("pat_loginDataVo");
    localStorage.removeItem("pat_Sel_LocationVo");
    localStorage.removeItem("pat_Photo");
    clearApiToken();

    if (countryVo) localStorage.setItem("countryVo", countryVo);
    if (clientLocURL) localStorage.setItem("clientLocURL", clientLocURL);
    if (selectedLocationVo) {
      localStorage.setItem("selectedLocationVo", selectedLocationVo);
    }
  } catch (e) {
    /* ignore */
  }
}

export function getSessionExpiresAt() {
  try {
    const exp = parseInt(localStorage.getItem(EXPIRES_KEY) || "0", 10);
    return exp > 0 ? exp : null;
  } catch (e) {
    return null;
  }
}
